import { IconButton } from "@mui/material";
import CheckBoxMultiCheck from "../../Sections/ElementsSections/CheckBoxMultiCheck";
import React, { useContext, useEffect, useRef, useState } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import { GetNextNumber } from "../../Tools/Requests";
import { SmetaModalsButtonsContext } from "../../Tools/modalManager";
import {
  getAssignMode,
  getAssignModeList,
  getNegativeID,
  getNewNumMode,
  getNewNumModeList,
  getPositiveID,
  getRange,
  getRangeList,
  getStrNumMode,
  getStrNumModeList,
} from "./Components/dialogDocNumberingComponents/dialogDocNumberingData";
import clsx from "clsx";
import dialogStyles from "./Components/dialogStyles.module.css";
import { DefaultDialogContentProps } from "./DialogAcctools";
import smetaAxios from "../../../SmetaAxios";
import { Prefixes } from "../../Tools/ProjectX";
import DialogDocNumberingCardItem from "./Components/dialogDocNumberingComponents/DialogDocNumberingCardItem";

interface Props {
  props: {
    res: {
      HasSNum?: "1";
      NewNumMode?: string;
      Current?: string;
      Range?: string;
      MaxNumber?: string;
      SNumber?: string;
      Mask?: string;
      StrNumMode?: string;
      CheckUnique?: string;
      AssignMode?: string;
      Rights?: string;
    };
    Sequence: string;
  };
}
interface Data {
  velocity: { value: string; id: number };
  newNumMode: { value: string; id: number };
  current: string;
  range: { value: string; id: number };
  maxNumber: string;
  assignMode: { value: string; id: number };
  sNumber: string;
  mask: string;
  strNumMode: { value: string; id: number };
  checkUnique: string;
}

/**
 * Контент диалога нумерации документов
 *
 * Поля компонента:
 *    1. [editorsValues, setEditorsValues] - объект состояний значений для редакторов
 *    2. [AssignModeDisabled, setAssignModeDisabled] - состояние активности редактора присвоения номера
 *    3. [showSub, setShowSub] -состояние видимости дополнительной карточки
 *    4. [needRefreshCurrent, setNeedRefreshCurrent] - состояние необходимости обновить номер
 *    5. [currentReadonly, setCurrentReadonly] - состояние активности редактора текущего номера
 *    6. editorBuffer - буффер редакторов для восстановления выбранных ранее значений
 *    7. currentChange - данные о ручном изменении текущего номера
 *
 * @param {Object} [props] объект с данными для компонента подразделений
 * @param {string} [props.Buttons] настрока видимых кнопок
 * @param {Object} [props.res] объект с данными для редакторов настройки нумерации
 * @param {string} [props.HasSNum] видимость дополнительной нумерации
 * @param {string} [props.NewNumMode] данные о генерации номера
 * @param {string} [props.Current] данные о текущем следующем номере
 * @param {string} [props.Range] данные о частоте обновления
 * @param {string} [props.MaxNumber] данные о максимальном номере
 * @param {string} [props.SNumber] данные об использовании дополнительной нумерации
 * @param {string} [props.Mask] данные о маске дополнительной нумерации
 * @param {string} [props.StrNumMode] данные о генерации номера в дополнительной нумерации
 * @param {string} [props.CheckUnique] проверка уникальности номера
 * @param {string} [props.AssignMode] данные о присвоении номера
 * @param {string} [props.Rights] права на редактирование
 *
 * @returns Возвращает контент диалога для настройки нумерации документов
 */
export default function DialogDocNumbering({ props }: Props & DefaultDialogContentProps) {
  const { res, Sequence: sequence, Buttons: buttons, hiddenButton } = props;

  const [editorsValues, setEditorsValues] = useState<Data>({
    velocity: { value: "", id: 0 },
    newNumMode: { value: "", id: 0 },
    current: "0",
    range: { value: "", id: 0 },
    maxNumber: "0",
    assignMode: { value: "", id: 0 },
    sNumber: "",
    mask: "",
    strNumMode: { value: "", id: 0 },
    checkUnique: "",
  });

  const [AssignModeDisabled, setAssignModeDisabled] = useState<boolean>(false);
  const [showSub, setShowSub] = useState<boolean>(false);
  const [needRefreshCurrent, setNeedRefreshCurrent] = useState<boolean>(false);
  const [currentReadonly, setCurrentReadonly] = useState<boolean>(false);

  const editorBuffer = useRef<{ assignMode?: { value: string; id: number } }>({});
  const currentChange = React.useRef<boolean>(false);

  const setButtons = useContext(SmetaModalsButtonsContext);

  useEffect(() => {
    if (hiddenButton) return;
    const modalButtons: { [key: number]: { label: string; onClick?: () => void } } = {};
    if (!buttons || Number(buttons) & 1) {
      modalButtons[1] = { label: "Установить", onClick: onSelect };
      modalButtons[2] = { label: "Закрыть" };
    }
    setButtons?.(modalButtons);
  }, [setButtons, currentChange.current, editorsValues]);

  /**
   * Инициализация всех настроек нумерации
   */
  const initData = () => {
    editorsValues.velocity = { value: sequence.split("\r\n")[0], id: 0 };

    if (res) {
      setShowSub(res.HasSNum === "1");
      if (res.NewNumMode === "1" || res.NewNumMode === "2") {
        setCurrentReadonly(true);
      } else {
        setCurrentReadonly(false);
      }

      editorsValues.newNumMode = {
        value: getNewNumMode()[Number(res.NewNumMode) || 0],
        id: Number(res.NewNumMode) || 0,
      };
      editorsValues.current = res.Current || "0";

      const defaultPeriod = res.NewNumMode === "1" || res.NewNumMode === "2" ? -17 : 0;
      editorsValues.range = {
        value: getRange()[Number(res.Range) || defaultPeriod],
        id: Number(res.Range) || defaultPeriod,
      };
      editorsValues.maxNumber = res.MaxNumber || "0";
      editorsValues.sNumber = res.SNumber || "0";
      editorsValues.mask = res.Mask || "";
      editorsValues.strNumMode = {
        value: getStrNumMode()[Number(res.StrNumMode) || 0],
        id: Number(res.StrNumMode) || 0,
      };
      editorsValues.checkUnique = res.CheckUnique || "0";

      if (!res.NewNumMode || res.NewNumMode === "3") {
        editorBuffer.current.assignMode = {
          value: getAssignMode()[0],
          id: 0,
        };
        setAssignModeDisabled(true);
        if (!res.NewNumMode) editorsValues.assignMode = { value: getAssignMode()[0], id: 0 };
        if (res.NewNumMode === "3") editorsValues.assignMode = { value: getAssignMode()[2], id: 2 };
      } else {
        const assignModeData = {
          value: getAssignMode()[Number(res.AssignMode) || 0],
          id: Number(res.AssignMode) || 0,
        };
        editorsValues.assignMode = assignModeData;
        editorBuffer.current.assignMode = assignModeData;
      }
    }

    setEditorsValues({ ...editorsValues });
  };
  useEffect(initData, []);

  /**
   * Управляет редактированием всех редакторов
   *
   * @param {Object}[data] - объект с данными редактирования
   * @param {string}[data.name] - ключ наименование редактора или чек-бокса
   */
  const onEdit = (data: { name: string }) => {
    const editFuncs: { [key: string]: (data: any) => void } = {
      velocity: editVelocity,
      newNumMode: editNewNumMode,
      current: editCurrent,
      range: editRange,
      maxNumber: editMaxNumber,
      assignMode: editAssignMode,
      sNumber: editSNumber,
      mask: editMask,
      strNumMode: editStrNumMode,
      checkUnique: editCheckUniquer,
    };
    editFuncs[data.name]?.(data);
    setEditorsValues({ ...editorsValues });
  };

  /**
   * Управляет редактированием последовательности
   *
   * @param {Object}[data] - объект с данными редактирования
   * @param {string}[data.value] - значение
   * @param {string}[data.tag] - id
   */
  const editVelocity = (data: { value: string; tag: number }) => {
    editorsValues.velocity = { value: data.value, id: data.tag };
  };

  /**
   * Управляет редактированием Генерациии номера, изменяет состояние редакторов присвоения номера и частоты обновления
   *
   * @param {Object}[data] - объект с данными редактирования
   * @param {string}[data.value] - значение
   * @param {string}[data.tag] - id
   */
  const editNewNumMode = (data: { value: string; tag: number }) => {
    editorsValues.newNumMode = { value: data.value, id: data.tag };

    if (!needRefreshCurrent) setNeedRefreshCurrent(true);

    if (data.tag === 1 || data.tag === 2) {
      setAssignModeDisabled(false);
      editorsValues.range = {
        value: getRange()[getNegativeID(editorsValues.range.id)],
        id: getNegativeID(editorsValues.range.id),
      };
      const id = editorBuffer.current.assignMode?.id;
      if (id !== undefined) {
        editorsValues.assignMode = { value: getAssignMode()[id], id: id };
      }
      setCurrentReadonly(true);
      return;
    }

    setAssignModeDisabled(true);
    editorsValues.range = {
      value: getRange()[getPositiveID(editorsValues.range.id)],
      id: getPositiveID(editorsValues.range.id),
    };
    if (data.tag === 0) {
      editorsValues.assignMode = { value: getAssignMode()[0], id: 0 };
    }
    if (data.tag === 3) {
      editorsValues.assignMode = { value: getAssignMode()[1], id: 1 };
    }
    setCurrentReadonly(false);
  };

  /**
   * Управляет редактированием следующего номера
   *
   * @param {Object}[data] - объект с данными редактирования
   * @param {string}[data.value] - значение
   */
  const editCurrent = (data: { value: string }) => {
    editorsValues.current = data.value;
    currentChange.current = true;
  };

  /**
   * Управляет редактированием частоты обновления
   *
   * @param {Object}[data] - объект с данными редактирования
   * @param {string}[data.value] - значение
   * @param {string}[data.tag] - id
   */
  const editRange = (data: { value: string; tag: number }) => {
    editorsValues.range = { value: data.value, id: data.tag };
    setNeedRefreshCurrent(true);
  };

  /**
   * Управляет редактированием максимального номера
   *
   * @param {Object}[data] - объект с данными редактирования
   * @param {string}[data.value] - значение
   */
  const editMaxNumber = (data: { value: string }) => {
    editorsValues.maxNumber = data.value;
    setNeedRefreshCurrent(true);
  };

  /**
   * Управляет редактированием присвоением номера
   *
   * @param {Object}[data] - объект с данными редактирования
   * @param {string}[data.value] - значение
   * @param {string}[data.tag] - id
   */
  const editAssignMode = (data: { value: string; tag: number }) => {
    editorsValues.assignMode = { value: data.value, id: data.tag };
    editorBuffer.current.assignMode = { value: data.value, id: data.tag };
  };

  /**
   * Управляет включением дополнительной нумерации
   *
   * @param {Object}[data] - объект с данными редактирования
   * @param {string}[data.CheckState] - значение чек-бокса
   */
  const editSNumber = (data: { CheckState: "1" | "0" }) => {
    editorsValues.sNumber = data.CheckState;
  };

  /**
   * Управляет редактированием маски
   *
   * @param {Object}[data] - объект с данными редактирования
   * @param {string}[data.value] - значение
   */
  const editMask = (data: { value: string }) => {
    editorsValues.mask = data.value;
  };

  /**
   * Управляет редактированием дополнительной генерацией номера
   *
   * @param {Object}[data] - объект с данными редактирования
   * @param {string}[data.value] - значение
   * @param {string}[data.tag] - id
   */
  const editStrNumMode = (data: { value: string; tag: number }) => {
    editorsValues.strNumMode = { value: data.value, id: data.tag };
  };

  /**
   * Управляет проверкой уникальности номера
   *
   * @param {Object}[data] - объект с данными редактирования
   * @param {string}[data.CheckState] - значение чек-бокса
   */
  const editCheckUniquer = (data: { CheckState: "1" | "0" }) => {
    editorsValues.checkUnique = data.CheckState;
  };

  /**
   * Обновляет текущий следующий номер в связи с новыми настройками
   */
  const onClickRefreshButton = async () => {
    setNeedRefreshCurrent(false);
    const json = await GetNextNumber({
      Name: editorsValues.velocity.value,
      Range: editorsValues.range.id,
      MaxNumber: editorsValues.maxNumber,
    });

    if (json.Number) {
      editorsValues.current = json.Number;
      setEditorsValues({ ...editorsValues });
    }
  };

  /**
   * Устанавливает новые настройки нумерации
   */
  const onSelect = () => {
    const params = {
      Name: editorsValues.velocity.value,
      GetNumMode: "1",
      AssignMode: editorsValues.assignMode.id,
      MaxNumber: editorsValues.maxNumber,
      NewNumMode: editorsValues.newNumMode.id,
      Range: editorsValues.range.id,
      SNumber: editorsValues.sNumber,
      Mask: editorsValues.mask,
      StrNumMode: editorsValues.strNumMode.id,
      CheckUnique: editorsValues.checkUnique,
      Current: currentChange.current ? editorsValues.current : undefined,
    };

    const config = {
      prefix: Prefixes.seq,
      method: "GET",
      url: "SequenceSetParams",
      params,
    };

    smetaAxios(config);
  };
  return (
    <div className={clsx(dialogStyles.mainDialogBlock)}>
      <div className={clsx(dialogStyles.fullDialogFlexContainer, dialogStyles.dialogDirectionColumn)}>
        <div className={clsx(dialogStyles.dialogDocNumberingMainCard)}>
          <div className={clsx(dialogStyles.dialogDocNumberingMainCardLabel)}>Основная нумерация</div>
          <DialogDocNumberingCardItem
            name="velocity"
            label="Последовательность:"
            value={editorsValues.velocity.value}
            id={editorsValues.velocity.id}
            onEdit={onEdit}
            list={sequence}
            readonly={true}
            editStyle={1}
            button={<IconButton className={clsx(dialogStyles.dialogDocNumberingDeleteButton)}>Удалить</IconButton>}
          />
          <DialogDocNumberingCardItem
            name="newNumMode"
            label="Генерация номера:"
            value={editorsValues.newNumMode.value}
            id={editorsValues.newNumMode.id}
            onEdit={onEdit}
            onList={getNewNumModeList}
            readonly={true}
            editStyle={1}
          />
          <DialogDocNumberingCardItem
            name="current"
            label="Следующий номер:"
            value={editorsValues.current}
            disabled={currentReadonly}
            onEdit={onEdit}
            editStyle={8}
            short={true}
            button={
              <IconButton
                className={clsx(dialogStyles.dialogDocNumberingRefreshButton)}
                title="Обновить текущую нумерацию"
                onClick={onClickRefreshButton}
              >
                <RefreshIcon fontSize={"small"} className={needRefreshCurrent ? "SaveIcon" : ""} />
              </IconButton>
            }
          />
          <DialogDocNumberingCardItem
            name="range"
            label="Частота обновления:"
            value={editorsValues.range.value}
            id={editorsValues.range.id}
            onEdit={onEdit}
            onList={() => getRangeList(editorsValues.newNumMode.value)}
            readonly={true}
            editStyle={1}
          />
          <DialogDocNumberingCardItem
            name="maxNumber"
            label="Установленный номер:"
            value={editorsValues.maxNumber}
            onEdit={onEdit}
            editStyle={8}
            short={true}
            hidden={!(editorsValues.range.id === 4 || editorsValues.range.id === -4)}
          />
          <DialogDocNumberingCardItem
            name="assignMode"
            label="Присвоение номера:"
            disabled={AssignModeDisabled}
            value={editorsValues.assignMode.value}
            id={editorsValues.assignMode.id}
            onEdit={onEdit}
            onList={getAssignModeList}
            readonly={true}
            editStyle={1}
          />
        </div>
        <div className={clsx(dialogStyles.dialogDocNumberingSubCard, !showSub && dialogStyles.dialogHidden)}>
          <div className={clsx(dialogStyles.dialogDocNumberingSubCardLabel)}>
            <div style={{ width: "27px" }}>
              <CheckBoxMultiCheck
                parentProps={{ name: "sNumber" }}
                CheckState={editorsValues.sNumber}
                onEdit={onEdit}
              />
            </div>
            <div> Дополнительная нумерация</div>
          </div>
          <DialogDocNumberingCardItem
            name="mask"
            label="Текстовая часть номера:"
            value={editorsValues.mask}
            onEdit={onEdit}
            afterLabel='(Символ "%" - номер)'
          />
          <DialogDocNumberingCardItem
            name="strNumMode"
            label="Генерация номера:"
            value={editorsValues.strNumMode.value}
            id={editorsValues.strNumMode.id}
            onEdit={onEdit}
            onList={getStrNumModeList}
            readonly={true}
            editStyle={1}
          />
          <div className={clsx(dialogStyles.dialogDocNumberingCardItem)}>
            <div className={clsx(dialogStyles.dialogDocNumberingCheckContainer)}>
              <CheckBoxMultiCheck
                parentProps={{ name: "checkUnique" }}
                CheckState={editorsValues.checkUnique}
                onEdit={onEdit}
              />
            </div>
            <div className={clsx(dialogStyles.dialogDocNumberingCheckLabel)}>
              Проверять уникальность введенного номера
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
